import { getFocusableElements } from './getFocusableElements';
import { getKey } from './getKey';

/**
 * A reusable focus trap utility that can be enabled/disabled via CSS custom properties.
 *
 * @example
 * ```css
 * .my-element {
 *   --focus-trap-enabled: 0;
 * }
 *
 * @media (max-width: 840px) {
 *   .my-element {
 *     --focus-trap-enabled: 1;
 *   }
 * }
 * ```
 *
 * @example
 * ```js
 * const trap = new FocusTrap(element, {
 *   selector: '.my-focusable-elements',
 *   customPropertyName: '--focus-trap-enabled'
 * });
 * ```
 */
export class FocusTrap {
  /** @type {HTMLElement} */
  element;

  /** @type {string} */
  selector;

  /** @type {string} */
  customPropertyName;

  /** @type {boolean} */
  isActive = false;

  /**
   * Creates a new FocusTrap instance.
   * @param {HTMLElement} element - The container element for the focus trap
   * @param {Object} options - Configuration options
   * @param {string} options.selector - CSS selector for focusable elements to include in the trap
   * @param {string} [options.customPropertyName='--focus-trap-enabled'] - CSS custom property name to check for enabled state
   */
  constructor(element, { selector, customPropertyName = '--focus-trap-enabled' }) {
    this.element = element;
    this.selector = selector;
    this.customPropertyName = customPropertyName;

    this._init();
  }

  /**
   * Initializes the focus trap by setting up observers and event listeners.
   * @private
   */
  _init() {
    this._checkEnabledState();

    // Watch for CSS custom property changes (e.g., from media queries)
    this._observeCustomProperty();

    // Also check on window resize (needed for older browsers)
    window.addEventListener('resize', this._checkEnabledState);
  }

  /**
   * Observes changes to the CSS custom property using MutationObserver and resize events.
   * @private
   */
  _observeCustomProperty() {
    if ('ResizeObserver' in window) {
      const resizeObserver = new ResizeObserver(() => {
        this._checkEnabledState();
      });
      resizeObserver.observe(this.element);
    }

    const mutationObserver = new MutationObserver(() => {
      this._checkEnabledState();
    });

    mutationObserver.observe(this.element, {
      attributes: true,
      attributeFilter: ['class', 'style'],
    });
  }

  /**
   * Checks the current value of the CSS custom property and enables/disables the trap accordingly.
   * @private
   */
  _checkEnabledState = () => {
    const computedStyle = getComputedStyle(this.element);
    const propertyValue = computedStyle.getPropertyValue(this.customPropertyName).trim();

    // Check if the property value indicates enabled (1)
    const shouldBeEnabled = propertyValue === '1';

    if (shouldBeEnabled && !this.isActive) {
      this.enable();
    } else if (!shouldBeEnabled && this.isActive) {
      this.disable();
    }
  };

  /**
   * @public
   */
  enable() {
    if (this.isActive) return;

    this.isActive = true;
    this.element.addEventListener('keydown', this._handleKeyDown);
  }

  /**
   * @public
   */
  disable() {
    if (!this.isActive) return;

    this.isActive = false;
    this.element.removeEventListener('keydown', this._handleKeyDown);
  }

  /**
   * Handles keydown events for focus trapping (Tab and Shift+Tab).
   * @private
   * @param {KeyboardEvent} event - The keyboard event
   */
  _handleKeyDown = (event) => {
    if (!getKey(event).isTab && !getKey(event).isShiftTab) return;

    const focusableElements = getFocusableElements(this.element, this.selector);
    const firstFocusableElement = focusableElements.first;
    const lastFocusableElement = focusableElements.last;

    // If there are no focusable elements, do nothing
    if (!firstFocusableElement || !lastFocusableElement) return;

    // Trap focus at the last element when tabbing forward: wrap focus to first element
    if (document.activeElement === lastFocusableElement && getKey(event).isTab) {
      event.preventDefault();
      firstFocusableElement.focus({ focusVisible: true });
    }

    // Trap focus at the first element when tabbing backward: wrap focus to last element
    if (document.activeElement === firstFocusableElement && getKey(event).isShiftTab) {
      event.preventDefault();
      lastFocusableElement.focus({ focusVisible: true });
    }
  };
}
